import { ChangeEvent } from "react";
import type { CameraState } from "./useCamera";

type Props = {
  state: CameraState;
  onFlip: () => void;
  onPickAudio: (id: string) => void;
  onZoomChange: (n: number) => void;
  onToggleMic: () => void;
  onToggleCam: () => void;
  disabled?: boolean;
};

export default function CameraControls(props: Props) {
  const { state, onFlip, onPickAudio, onZoomChange, onToggleMic, onToggleCam, disabled } = props;
  const handleAudio = (e: ChangeEvent<HTMLSelectElement>) => onPickAudio(e.target.value);

  return (
    <div className="cam-controls" aria-label="Camera controls">
      <div className="cam-controls__matrix">
        <button
          type="button"
          className="cam-controls__btn"
          onClick={onToggleMic}
          aria-pressed={state.micEnabled}
          title={state.micEnabled ? "Mute microphone" : "Unmute microphone"}
        >
          {state.micEnabled ? "Mic on" : "Mic off"}
        </button>

        <button
          type="button"
          className="cam-controls__btn"
          onClick={onToggleCam}
          aria-pressed={state.camEnabled}
          title={state.camEnabled ? "Hide camera" : "Show camera"}
        >
          {state.camEnabled ? "Camera on" : "Camera off"}
        </button>

        <button
          type="button"
          className="cam-controls__btn"
          onClick={onFlip}
          disabled={disabled}
          title="Switch between front and back camera"
        >
          Flip ({state.facing === "user" ? "front" : "back"})
        </button>
        {state.zoom.supported ? (
          <div className="cam-controls__zoom">
            <span className="cam-controls__zoom-label">Zoom</span>
            <div className="cam-controls__zoom-row">
              <button
                type="button"
                className="cam-controls__btn cam-controls__btn--small"
                onClick={() => onZoomChange(state.zoom.value - state.zoom.step)}
                disabled={disabled || state.zoom.value <= state.zoom.min}
                aria-label="Zoom out"
              >−</button>
              <input
                type="range"
                min={state.zoom.min}
                max={state.zoom.max}
                step={state.zoom.step}
                value={state.zoom.value}
                onChange={(e) => onZoomChange(parseFloat(e.target.value))}
                disabled={disabled}
                aria-label="Zoom level"
              />
              <button
                type="button"
                className="cam-controls__btn cam-controls__btn--small"
                onClick={() => onZoomChange(state.zoom.value + state.zoom.step)}
                disabled={disabled || state.zoom.value >= state.zoom.max}
                aria-label="Zoom in"
              >+</button>
              <span className="cam-controls__zoom-val">{state.zoom.value.toFixed(1)}×</span>
            </div>
          </div>
        ) : (
          <div className="cam-controls__zoom-unsupported">
            <small>Zoom not supported by this camera/browser.</small>
          </div>
        )}
      </div>

      <label className="cam-controls__select">
        <span>Microphone</span>
        <select
          value={state.activeAudioId ?? ""}
          onChange={handleAudio}
          disabled={disabled || state.audioDevices.length === 0}
        >
          {state.audioDevices.length === 0 && <option value="">No microphones</option>}
          {state.audioDevices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
